import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getRoutes } from "../../../utils/get-routes";
import { getGroupRoutes } from "../../../utils/get-group-routes";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { setOffset } from "../../../store/auth/slice";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CatalogIcon,
  ShoppingCart as SalesIcon,
  Article as ContentIcon,
  Business as CompanyIcon,
  Group as PeopleIcon,
  Folder as OtherIcon,
} from "@mui/icons-material";
import clsx from "clsx";

import styles from "./styles.module.scss";

const GROUP_ICONS = {
  Catalog: CatalogIcon,
  Sales: SalesIcon,
  Content: ContentIcon,
  Company: CompanyIcon,
  People: PeopleIcon,
};

const Sidebar = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const adminRole = localStorage.getItem("adminRole");
  const { isAuth } = useSelector((state) => state.auth);

  const [isOpen, setIsOpen] = useState(false);
  const toggleSidebar = () => setIsOpen((prev) => !prev);

  const routes = useMemo(
    () => getRoutes(adminRole, isAuth),
    [adminRole, isAuth]
  );

  const groups = useMemo(() => getGroupRoutes(routes), [routes]);

  const activeGroupName = useMemo(
    () =>
      groups.find((group) =>
        group.items.some((item) => location.pathname.startsWith(item.path))
      )?.name,
    [groups, location.pathname]
  );

  const [openGroup, setOpenGroup] = useState(activeGroupName);

  useEffect(() => {
    setOpenGroup(activeGroupName);
  }, [activeGroupName]);

  const toggleGroup = (name) => {
    if (!isOpen) {
      setIsOpen(true);
      setOpenGroup(name);
      return;
    }
    setOpenGroup((prev) => (prev === name ? null : name));
  };

  useOutsideClick(sidebarRef, () => setIsOpen(false));

  if (!isAuth && !adminRole) {
    return null;
  }

  const cleanOffset = () => {
    dispatch(setOffset(0));
  };

  return (
    <nav
      ref={sidebarRef}
      className={clsx(styles.sidebar, { [styles.open]: isOpen })}
    >
      <div className={styles.sidebar_inner}>
        <header className={styles.sidebar_header}>
          <button
            type="button"
            onClick={toggleSidebar}
            className={styles.sidebar_burger}
          >
            {isOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </header>

        <nav className={styles.sidebar_menu}>
          {groups.map((group) => {
            const isGroupOpen = openGroup === group.name;
            const GroupIcon = GROUP_ICONS[group.name] || OtherIcon;

            return (
              <div key={group.name} className={styles.sidebar_group}>
                <button
                  type="button"
                  title={group.name}
                  onClick={() => toggleGroup(group.name)}
                  className={clsx(styles.sidebar_group_header, {
                    [styles.expanded]: isGroupOpen,
                    [styles.collapsed]: !isOpen,
                  })}
                >
                  <span className={styles.sidebar_group_label}>
                    <GroupIcon className={styles.sidebar_group_icon} />
                    <span className={styles.sidebar_group_name}>
                      {group.name}
                    </span>
                  </span>
                  <ExpandMoreIcon className={styles.sidebar_group_chevron} />
                </button>

                <div
                  className={clsx(styles.sidebar_group_list_wrap, {
                    [styles.expanded]: isGroupOpen,
                    [styles.collapsed]: !isOpen,
                  })}
                >
                  <ul className={styles.sidebar_group_list}>
                    {group.items.map((item) => (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          title={item.name}
                          onClick={() => cleanOffset()}
                          className={({ isActive }) =>
                            clsx(styles.sidebar_button, {
                              [styles.active]: isActive,
                            })
                          }
                        >
                          <img
                            src={item.icon}
                            alt={item.name}
                            width={20}
                            height={20}
                          />
                          {item.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </nav>
  );
};

export default Sidebar;
